import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Layout } from "./components/Layout";
import { TokenGate } from "./components/TokenGate";
import { getSession } from "./lib/auth";

// Lazy-load every page so the initial bundle carries only the shell + the
// Overview (the most common landing). Each route becomes a separate chunk
// fetched on first visit and cached by Vite's content-hashed filenames.
const Overview = lazy(() =>
  import("./pages/Overview").then((m) => ({ default: m.Overview })),
);
const Sites = lazy(() =>
  import("./pages/Sites").then((m) => ({ default: m.Sites })),
);
const Health = lazy(() =>
  import("./pages/Health").then((m) => ({ default: m.Health })),
);
const SiteDetail = lazy(() =>
  import("./pages/SiteDetail").then((m) => ({ default: m.SiteDetail })),
);
const HostingAccounts = lazy(() =>
  import("./pages/HostingAccounts").then((m) => ({ default: m.HostingAccounts })),
);
const Domains = lazy(() =>
  import("./pages/Domains").then((m) => ({ default: m.Domains })),
);
const Expirations = lazy(() =>
  import("./pages/Expirations").then((m) => ({ default: m.Expirations })),
);
const Import = lazy(() =>
  import("./pages/Import").then((m) => ({ default: m.Import })),
);
const SettingsSMTP = lazy(() =>
  import("./pages/SettingsSMTP").then((m) => ({ default: m.SettingsSMTP })),
);
const SettingsWhatsApp = lazy(() =>
  import("./pages/SettingsWhatsApp").then((m) => ({ default: m.SettingsWhatsApp })),
);
const Audit = lazy(() =>
  import("./pages/Audit").then((m) => ({ default: m.Audit })),
);
const Users = lazy(() =>
  import("./pages/Users").then((m) => ({ default: m.Users })),
);
const Security = lazy(() =>
  import("./pages/Security").then((m) => ({ default: m.Security })),
);
const SecurityAudit = lazy(() =>
  import("./pages/SecurityAudit").then((m) => ({ default: m.SecurityAudit })),
);

function PageFallback() {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <span
        className="inline-block h-3 w-3 animate-pulse rounded-full bg-slate-300"
        aria-hidden
      />
      Carregando…
    </div>
  );
}

// EnrollmentGuard intercepta usuários com session enrollment-only
// (Fase C — master sem TOTP). Força navegação pra /security e bloqueia
// qualquer outra rota até o user completar o cadastro do 2FA.
function EnrollmentGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const sess = getSession();
  if (sess?.must_enroll && location.pathname !== "/security") {
    return <Navigate to="/security" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <TokenGate>
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <EnrollmentGuard>
            <Routes>
              <Route path="/" element={<Navigate to="/overview" replace />} />
              <Route path="/overview" element={<Overview />} />
              <Route path="/health" element={<Health />} />
              <Route path="/sites" element={<Sites />} />
              <Route path="/sites/:id" element={<SiteDetail />} />
              <Route path="/hosting-accounts" element={<HostingAccounts />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/expirations" element={<Expirations />} />
              <Route path="/import" element={<Import />} />
              <Route path="/settings/smtp" element={<SettingsSMTP />} />
              <Route path="/settings/whatsapp" element={<SettingsWhatsApp />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/users" element={<Users />} />
              <Route path="/security" element={<Security />} />
              <Route path="/security-audit" element={<SecurityAudit />} />
              <Route path="*" element={<Navigate to="/overview" replace />} />
            </Routes>
          </EnrollmentGuard>
        </Suspense>
      </Layout>
    </TokenGate>
  );
}
